import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Typography } from "@mui/material";
import { useAuth } from "@sal/authentication.react";
import { getApiErrorMessage } from "code/apiError";
import { stripPhonePrefix } from "code/formatHelpers";
import { normalizePhoneWithPrefix } from "code/phoneValidation";
import { useNotification } from "components/NotificationProvider";
import PhoneInput from "components/PhoneInput";
import ResponsiveButton from "components/ResponsiveButton";
import SpinnerOverlay from "components/SpinnerOverlay";
import TextInput from "components/TextInput";
import { DEFAULT_PHONE_CODE } from "constants/CountryCodes";
import { useGetCompanyDetails, useUpdateCompanyDetails } from "hooks/companyDetails";
import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";
import { CompanyDetailsFormValues, CompanyDetailsSchema } from "validations/CompanyDetailsSchema";

export default function CompanyDetailsTab() {
	const auth = useAuth();
	const canWrite = auth.userHasPermission("Accounts", "Write");
	const { data, isDataLoading, dataError, mutateData } = useGetCompanyDetails();
	const { updateCompanyDetails, isUpdating } = useUpdateCompanyDetails();
	const { showNotification } = useNotification();
	// react-hook-form's handleSubmit validates asynchronously, so a rapid second click can
	// re-enter this callback before `isUpdating` re-renders the disabled Save button.
	const isSavingRef = useRef(false);

	const { control, handleSubmit, reset } = useForm<CompanyDetailsFormValues>({
		resolver: zodResolver(CompanyDetailsSchema),
		defaultValues: { email: "", phoneNumber: "" }
	});

	useEffect(() => {
		if (data) {
			reset({ email: data.email ?? "", phoneNumber: stripPhonePrefix(data.phone) });
		}
	}, [data, reset]);

	const onSave = handleSubmit(async (values) => {
		if (isSavingRef.current) {
			return;
		}
		isSavingRef.current = true;
		try {
			const localNumber = normalizePhoneWithPrefix(DEFAULT_PHONE_CODE, values.phoneNumber);
			await updateCompanyDetails({
				email: values.email,
				phone: `${DEFAULT_PHONE_CODE}${localNumber}`
			});
			await mutateData();
			showNotification({ message: "Company details updated", severity: "success" });
		} catch (error) {
			showNotification({
				message: getApiErrorMessage(error, "Unable to update company details"),
				severity: "error"
			});
		} finally {
			isSavingRef.current = false;
		}
	});

	if (isDataLoading) {
		return <SpinnerOverlay open />;
	}

	if (dataError || !data) {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 2
				}}
			>
				<Typography color="error.main">Unable to load company details.</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				flex: 1,
				minHeight: 0,
				overflow: "auto",
				display: "flex",
				flexDirection: "column",
				alignItems: { xs: "stretch", md: "center" },
				p: 2.5
			}}
		>
			<Box
				sx={{
					bgcolor: "background.paper",
					width: { xs: "100%", md: "480px" },
					flexGrow: { xs: 1, md: 0 },
					flexShrink: 0,
					my: { xs: 0, md: "auto" },
					display: "flex",
					flexDirection: "column",
					border: 1,
					borderColor: "divider",
					borderRadius: 1
				}}
			>
				{/* Header */}
				<Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, py: 1.75 }}>
					<Typography variant="text20Bold">Company Details</Typography>
				</Box>
				<Box
					sx={{
						px: { xs: 2, md: 3 },
						py: { xs: 3, md: 4 },
						flexGrow: { xs: 1, md: 0 }
					}}
				>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
						<TextInput label="Account Number (FRI Code)" value={data.friCode ?? ""} disabled />
						<TextInput label="Name" value={data.name ?? ""} disabled />
						<TextInput label="Street Address" value={data.streetAddress ?? ""} disabled />
						<TextInput label="Postal Address" value={data.postalAddress ?? ""} disabled />
						<TextInput label="Post Box" value={data.postBox ?? ""} disabled />
						<Controller
							name="email"
							control={control}
							render={({ field, fieldState }) => (
								<TextInput
									label="Email"
									required
									value={field.value}
									onChange={field.onChange}
									onBlur={field.onBlur}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									disabled={!canWrite}
								/>
							)}
						/>
						<Controller
							name="phoneNumber"
							control={control}
							render={({ field, fieldState }) => (
								<PhoneInput
									label="Phone Number"
									required
									labelTooltip="This number will be used as a default for non-portal bookings (e.g. Agent Bookings, existing forward bookings), and can be edited on the booking"
									value={field.value}
									onChange={field.onChange}
									onBlur={() => {
										field.onChange(normalizePhoneWithPrefix(DEFAULT_PHONE_CODE, field.value ?? ""));
										field.onBlur();
									}}
									error={!!fieldState.error}
									helperText={fieldState.error?.message}
									disabled={!canWrite}
								/>
							)}
						/>
					</Box>
				</Box>

				{canWrite && (
					<Box
						sx={{
							borderTop: 1,
							borderColor: "divider",
							display: "flex",
							justifyContent: "flex-end",
							p: 3,
							mt: "auto"
						}}
					>
						<ResponsiveButton variant="contained" onClick={onSave} disabled={isUpdating}>
							Save
						</ResponsiveButton>
					</Box>
				)}
			</Box>
		</Box>
	);
}
